import dayjs from 'dayjs';
import { FC } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Queue from '@/shared/types/fulfilment/queue.type';
import Order from '@/shared/types/order/order.type';
import formatCurrency from '@/shared/utils/formatCurrency';

import ProductTableRow from './CustomsProductTableForm/types/productTableRow.type';
import usePackagingDetailsTableForm_v2 from './PackagingDetailsTableForm_v2/hooks/usePackagingDetailsTableForm_v2';
import usePickupDetailsForm from './PickupDetailsForm/hooks/usePickupDetailsForm';
import useSetOriginForm from './SetOriginForm/hooks/useSetOriginForm';
import useTaxRecipientForm from './TaxRecipientForm/hooks/useTaxRecipientForm';

type Args = {
  order: Order;
  customProductsTableData: ProductTableRow[];
  selectedQueues?: Queue[];
  packagesDetailsTableState: ReturnType<typeof usePackagingDetailsTableForm_v2>;
  pickupDetailsState: ReturnType<typeof usePickupDetailsForm>;
  taxRecipientState: ReturnType<typeof useTaxRecipientForm>;
  originFormState: ReturnType<typeof useSetOriginForm>;
  isCustoms?: boolean;
};

const Review: FC<Args> = ({
  order,
  customProductsTableData,
  selectedQueues,
  packagesDetailsTableState,
  pickupDetailsState,
  taxRecipientState,
  originFormState,
  isCustoms,
}) => {
  const {
    delivery_name,
    delivery_address,
    delivery_address2,
    delivery_city,
    delivery_country,
    delivery_postcode,
    delivery_state,
  } = order;

  const { packages } = packagesDetailsTableState;
  const { pickupDetailsForm } = pickupDetailsState;
  const { taxRecipientForm, taxRecipientFormOptional } = taxRecipientState;
  const { pickOriginForm } = originFormState;

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant='body1'>Ship To: {delivery_name || '-'}</Typography>
        {!delivery_address &&
        !delivery_address2 &&
        !delivery_city &&
        !delivery_country &&
        !delivery_postcode &&
        !delivery_state ? (
          <Typography variant='body2' color='text.secondary'>
            No shipping details provided
          </Typography>
        ) : (
          <>
            <Typography variant='body2' color='text.secondary'>
              Address 1: {delivery_address || '-'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Address 2: {delivery_address2 || '-'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              City: {delivery_city || '-'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Country: {delivery_country || '-'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Postcode: {delivery_postcode || '-'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              State: {delivery_state || '-'}
            </Typography>
          </>
        )}
      </Stack>

      {isCustoms && (
        <>
          <Stack>
            <Typography variant='body1'>Customs</Typography>
            <Typography variant='body2' color='text.secondary'>
              Fulfilment Origin: {pickOriginForm.fulfilmentOrigin}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Source: {pickOriginForm.source}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Use Custom Broker: {String(pickOriginForm.useCustomsBroker)}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Use Carrier: {String(pickOriginForm.use_carrier)}
            </Typography>
          </Stack>

          <Stack>
            <Typography variant='body1'>Customs - Tax Recipient</Typography>
            <Typography variant='body2' color='text.secondary'>
              Type: {taxRecipientForm.type}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Reason For Export: {taxRecipientForm.reason_for_export}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Shipper Tax Identifier:{' '}
              {taxRecipientFormOptional.shipper_tax_identifier}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Receiver Tax Identifier:{' '}
              {taxRecipientFormOptional.receiver_tax_identifier}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Additional Remarks: {taxRecipientFormOptional.additional_remarks}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Comments: {taxRecipientFormOptional.comments}
            </Typography>
          </Stack>
        </>
      )}

      <Stack>
        <Typography variant='body1'>Packages</Typography>
        <Stack spacing={1}>
          {packages.map((packageItem) => (
            <Stack key={packageItem.package_id}>
              <Typography
                variant='body2'
                color='text.secondary'
                fontWeight={600}
              >
                {`Package ${packageItem.package_number}`}:
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
              >{`Weight: ${packageItem.package_weight?.value} ${packageItem.package_weight?.unit}`}</Typography>
              <Typography variant='body2' color='text.secondary'>
                {`Dimensions:
               height: ${packageItem.package_cuboid.h},
               length: ${packageItem.package_cuboid.l},
               width: ${packageItem.package_cuboid.w},
               unit: ${packageItem.package_cuboid.unit}
              `}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
              >{`Description: ${packageItem.package_description}`}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Stack>
        <Typography variant='body1'>Pickup Details</Typography>

        {pickupDetailsForm.pre_scheduled_pickup ? (
          <Typography variant='body2' color='text.secondary'>
            Prescheduled Pickup:{' '}
            {String(pickupDetailsForm.pre_scheduled_pickup)}
          </Typography>
        ) : (
          <>
            <Typography variant='body2' color='text.secondary'>
              {`Ready after: ${dayjs(pickupDetailsForm.ts_ready_at).format('YYYY-MM-DD h:mm:ss A')}`}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {`Ready until: ${dayjs(pickupDetailsForm.ts_ready_until).format('YYYY-MM-DD h:mm:ss A')}`}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {`Pickup Location: ${pickupDetailsForm.pickup_location}`}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {`Contact Name: ${pickupDetailsForm.contact_name}`}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {`Contact: ${pickupDetailsForm.contact_phone_number.extension} ${pickupDetailsForm.contact_phone_number.number}`}
            </Typography>
          </>
        )}
      </Stack>

      <Stack>
        <Typography variant='body1'>Products</Typography>
        {isCustoms ? (
          <Stack spacing={1}>
            {customProductsTableData.map((item) => (
              <Stack key={item.fulfilment_queue_id}>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color='text.secondary'
                >
                  {`${item.product_name}${item.variant_name !== '' && item.variant_name !== '_default' ? `: ${item.variant_name}: ` : ''}: ${item.product_qty} x  ${formatCurrency(parseFloat(item.unit_price.value))} ${item.unit_price.currency}`}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {`Origin: ${item.country_of_origin}`}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {`Weight: ${item.weight?.value} ${item.weight?.unit}`}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {`Description: ${item.product_description}`}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {`Assigned Package: Package ${packages.find((packageItem) => packageItem.package_id === item.assign_package)?.package_number}`}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack spacing={1}>
            {selectedQueues?.map((item) => (
              <Stack key={item.fulfilment_queue_id}>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color='text.secondary'
                >
                  {`${item.product_name}: ${item.product_qty} x  ${formatCurrency(parseFloat(item.pre_tax_price) / parseInt(item.product_qty))} ${formatCurrency(parseFloat(item.pre_tax_price))}`}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {`Description: ${item.product_description}`}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default Review;
